import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStateContext } from "/src/features/auth/context/authContext";
import ActionTypeBadge from "/src/features/administration/logs/components/ActionTypeBadge/ActionTypeBadge";
import LogMessage from "/src/features/administration/logs/components/LogMessage/LogMessage";
import { formatDateWithoutSeconds } from "/src/utils/dateWithoutSeconds";
import getCutToken from "/src/utils/getCutToken";
import accountService from "../../services/accountService";
import "./MyActivity.css";

const CONVERSATION_IN_BRACKETS = /\[([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/gi;
const MAX_RECENT_CONVERSATIONS = 8;

// Recent conversations this user has worked on, derived straight from
// their own activity log messages (every one already carries its
// conversation id in brackets) — no extra request needed.
function extractRecentConversations(logs) {
    const seen = new Set();
    const conversations = [];

    for (const log of logs) {
        const matches = log.message.matchAll(CONVERSATION_IN_BRACKETS);
        for (const match of matches) {
            const callId = match[1];
            if (seen.has(callId)) continue;
            seen.add(callId);
            conversations.push({ callId, lastActivityAt: log.created_at });
            if (conversations.length >= MAX_RECENT_CONVERSATIONS) return conversations;
        }
    }

    return conversations;
}

function MyActivity() {
    const { user } = useAuthStateContext();
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchActivity = async () => {
            const [response, error] = await accountService.getMyActivity({ page: 1, limit: 50 });
            if (!error && response) {
                setLogs(response.data || []);
            }
            setIsLoading(false);
        };
        fetchActivity();
    }, []);

    const recentConversations = useMemo(() => extractRecentConversations(logs), [logs]);

    if (isLoading) {
        return <p className="my-activity_empty">Loading your activity...</p>;
    }

    if (logs.length === 0) {
        return <p className="my-activity_empty">No activity yet.</p>;
    }

    return (
        <div className="my-activity">
            {recentConversations.length > 0 && (
                <div className="my-activity_conversations">
                    <span className="my-activity_subheading">Recently worked on</span>
                    <div className="my-activity_conversations-list">
                        {recentConversations.map((conv) => (
                            <span
                                key={conv.callId}
                                className="my-activity_conversation-chip"
                                onClick={() => navigate(`/supervision/conversation/profil/${conv.callId}`)}
                                title="Open this conversation"
                            >
                                {getCutToken(conv.callId)}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <span className="my-activity_subheading">Recent activity</span>
            <div className="my-activity_feed">
                {logs.map((log) => (
                    <div key={log.id} className="my-activity_item">
                        <div className="my-activity_item-badge">
                            <ActionTypeBadge value={log.action_type} />
                        </div>
                        <div className="my-activity_item-content">
                            <LogMessage message={log.message} viewerEmail={user?.email} />
                            <span className="my-activity_date">{formatDateWithoutSeconds(log.created_at, false)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default MyActivity;
